import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faBookOpen } from '@fortawesome/pro-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import '../assignments.scss';
import PropTypes from 'prop-types';
import CustomToast from '../../../components/Toast';
import AttachResources from './questionDetailComponents/AttachResources';
import { useGetCastleResourceMutation, useGetHtmlContentMutation } from '../../../features/authentication/assignmentApislice';
import QuestionPreviewTypes from '../../../enums/questionPreviewTypes';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { faMinusCircle, faPlusCircle, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { useGetQuestionFilesMutation, useGetQuestionHintMutation, useGetQuestionMCAnswerMutation, useGetQuestionReasonMutation, useGetQuestionSampleAnswerMutation, useGetQuestionTextMutation, useSetQuestionPointMutation } from '../../../features/authentication/questionApiSlice';
import SkeletonGrid from '../../../components/SkeletonLoader/SkeletonGrid';
import BookletViewer from './questionDetailComponents/BookletViewer';
import ProgressButton from '../../../components/ProgressButton';
import usePrintPdfGenerator from '../../../utils/PrintPdfGenerator';
import PageSize from '../../../enums/pageSize';
import Languages from '../../../enums/languages';
import LanguageToggleSwitch from './LanguageToggleSwitch';

const fitHtmlResourceTables = (container) => {
  container.querySelectorAll('table').forEach((table) => {
    table.style.setProperty('width', 'auto', 'important');
    table.style.setProperty('table-layout', 'fixed', 'important');

    const rows = Array.from(table.rows);
    const columnCount = rows[0]?.cells.length || 0;
    const columnWidths = Array.from({ length: columnCount }, (_, columnIndex) => {
      for (const row of rows) {
        const cell = row.cells[columnIndex];
        const width = parseFloat(cell?.style.width || cell?.getAttribute('width')) || 0;
        if (width > 0) return width;
      }
      return 0;
    });
    const hasAllColumnWidths = columnWidths.length > 0 && columnWidths.every((width) => width > 0);
    const totalColumnWidth = columnWidths.reduce((total, width) => total + width, 0);

    if (hasAllColumnWidths) {
      rows.forEach((row) => {
        if (row.cells.length !== columnWidths.length) return;

        Array.from(row.cells).forEach((cell, index) => {
          cell.style.setProperty(
            'width',
            `${(columnWidths[index] / totalColumnWidth) * 100}%`,
            'important'
          );
        });
      });
    }

    table.querySelectorAll('td, th').forEach((cell) => {
      cell.style.setProperty('box-sizing', 'border-box', 'important');
      cell.style.setProperty('min-width', '0', 'important');
      cell.style.setProperty('white-space', 'normal', 'important');
      cell.style.setProperty('overflow-wrap', 'anywhere', 'important');
      cell.style.setProperty('word-break', 'break-word', 'important');
    });

    table.querySelectorAll('img').forEach((image) => {
      image.style.setProperty('max-width', '100%', 'important');
      image.style.setProperty('height', 'auto', 'important');
    });
  });
};

const fitHtmlQuestionTablesForPrint = (container) => {
  container.querySelectorAll('.questionWrapper table').forEach((table) => {
    const cells = table.rows[0]?.cells;
    if (!cells || cells.length < 2) return;

    const hasImageInEveryCell = Array.from(cells).every((cell) => cell.querySelector('img'));
    if (!hasImageInEveryCell) return;

    const listIndentInches = 0.25;
    const imageWidthInches = (6 - (cells.length * listIndentInches)) / cells.length;

    Array.from(cells).forEach((cell) => {
      cell.querySelectorAll('ol').forEach((list) => {
        list.style.setProperty('margin', '0', 'important');
        list.style.setProperty('padding-left', `${listIndentInches}in`, 'important');
      });

      cell.querySelectorAll('img').forEach((image) => {
        image.style.setProperty('max-width', `min(100%, ${imageWidthInches}in)`, 'important');
        image.style.setProperty('height', 'auto', 'important');
      });
    });
  });
};

const fitHtmlResourceContent = (content) => {
  const container = document.createElement('div');
  container.innerHTML = content;
  fitHtmlResourceTables(container);
  return container.innerHTML;
};

const QuestionDetail = (props) => {
  const resourceLoadResolverRef = useRef(null);
  const [hintDetails, setHintDetails] = useState(false);
  const [answerDetails, setAnswerDetails] = useState(false);
  const [reasonDetail, setReasonDetail] = useState(false);
  const [sampleAnswerDetails, setSampleAnswerDetails] = useState(false);
  const [optionDetails, setOptionDetails] = useState(false);
  const formCheckRef = useRef(null);
  const [readingPassage, setReadingPassage] = useState(false);
  const [type1, setType1] = useState(true);
  const [type2, setType2] = useState(true);
  const [tagAttributeStandard, setTagAttributeStandard] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [updatedMaxPoint, setUpdatedMaxPoint] = useState(null);
  const [maxPointUpdateMessage, setMaxPointUpdateMessage] = useState('');
  const [updatedMaxPointError, setUpdatedMaxPointError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [htmResources, setHtmResources] = useState([]);
  const [audioResources, setAudioResources] = useState([]);
  const [castleResourceData, setCastleResourceData] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState({
    question: Languages.English,
    hint: Languages.English,
    reason: Languages.English,
    mcAnswer: Languages.English,
    sampleAnswer: Languages.English,
    resources: Languages.English
  });
  const [getHtmlContent] = useGetHtmlContentMutation();
  const [setQuestionPoint] = useSetQuestionPointMutation();
  const { printPDF, isPdfGenerating } = usePrintPdfGenerator();
  const [getQuestionHint, { isLoading: isHintLoading }] = useGetQuestionHintMutation();
  const [getQuestionReason, { isLoading: isReasonLoading }] = useGetQuestionReasonMutation();
  const [getQuestionSampleAnswer, { isLoading: isSampleAnswerLoading }] = useGetQuestionSampleAnswerMutation();
  const [getQuestionMCAnswer, { isLoading: isMCAnswerLoading }] = useGetQuestionMCAnswerMutation();
  const [getQuestionText, { isLoading: isQuestionTextLoading }] = useGetQuestionTextMutation();
  const [getQuestionResources, { isLoading: isQuestionResourceLoading }] = useGetQuestionFilesMutation();
  const [getCastleResource] = useGetCastleResourceMutation();

  useEffect(() => {
    setCastleResourceData(null);
  }, [props.questionDetails?.questionId]);

  useEffect(() => {
    let answerLabel = document.querySelectorAll('.answer-label');
    answerLabel.forEach((label) => {
      let paragraphs = label.querySelectorAll('p');
      paragraphs.forEach((paragraph) => {
        let parent = paragraph.parentNode;
        while (paragraph.firstChild) {
          parent.insertBefore(paragraph.firstChild, paragraph);
        }
        parent.removeChild(paragraph);
      });
    });
  }, [answerDetails]);

  useEffect(() => {
    if (props.isLoading == true) {
      setType2(true);
      setType1(true);
    }
    else {
      props.questionDetails?.type === 'MC' || props.questionDetails?.type === 'FillIn'
        ? setType1(true)
        : setType1(false);
      props.questionDetails?.type === 'CR' ? setType2(true) : setType2(false);
    }
    const htmResources = (props.questionDetails?.question?.externalFiles
      ?.filter(resource => resource.filePath?.endsWith('.htm') || resource.filePath?.endsWith('.html'))
      .map(item => ({
        path: item.filePath,
        resourceId: item.resourceId
      })) ?? []);
    setHtmResources(htmResources);

    const audioResources = props.questionDetails?.question?.externalFiles
      ?.filter(resource => resource.filePath?.endsWith('.mp3'))
      .map(item => {
        return { path: item.filePath, resourceId: item.resourceId };
      });
    setAudioResources(audioResources);

    setReadingPassage(htmResources?.length > 0);
  }, [props.isLoading]);

  const fetchCastleResources = async (questionId) => {
    try {
      const option = {
        questionId: questionId ? questionId : props.questionDetails?.questionId,
        assignmentId: props.assignmentId,
        courseId: props.questionDetails?.questionCourseId
      };
      const response = await getCastleResource(option).unwrap();
      setCastleResourceData(response);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const handleResourceLoadComplete = () => {
    if (resourceLoadResolverRef.current) {
      resourceLoadResolverRef.current();
      resourceLoadResolverRef.current = null;
    }
  };

  const handlePrint = async () => {
    if (isPdfGenerating) return;

    if (!castleResourceData) {
      await fetchCastleResources();
      await new Promise(resolve => {
        resourceLoadResolverRef.current = resolve;
      });
    }

    const {
      resources: loadedHtmlResources
    } = await loadAllHtmlResources();

    await new Promise(resolve => setTimeout(resolve, 300));
    const contentWrapper = document.getElementById('content');
    const contentClone = contentWrapper.cloneNode(true);
    contentClone.querySelectorAll('.questionWrapper, .reasonWrapper').forEach(fitHtmlResourceTables);

    const header1 = document.createElement('header');
    header1.textContent = 'Question Details';
    header1.style.fontWeight = 'bold';
    header1.style.fontSize = '24px';
    header1.style.marginBottom = '16px';

    contentClone.insertBefore(header1, contentClone.firstChild);
    const button = contentClone.querySelector('#printButton');
    if (button !== null)
      button.style.display = 'none';

    const divs = contentClone.querySelectorAll('div.toggleDive');
    divs.forEach((div) => {
      if (div.classList.contains('d-none')) {
        div.classList.remove('d-none');
      }
    });

    const divsNoPrint = contentClone.querySelectorAll('.no-print');
    divsNoPrint.forEach((div) => {
      div.classList.add('d-none');
    });

    const divsMcAnswer = contentClone.querySelectorAll('.mc-answer');
    divsMcAnswer.forEach((div) => {
      div.classList.remove('d-none');
    });

    const buttonRoundedCircle = contentClone.querySelectorAll('button.rounded-circle');
    buttonRoundedCircle.forEach((div) => {
      div.classList.add('d-none');
    });

    const svgElements = contentClone.querySelectorAll('svg');

    const hideIcons = ['fa-circle-question', 'fa-circle-plus', 'fa-circle-minus'];

    svgElements.forEach((svgElement) => {
      if ([...svgElement.classList].some(className => hideIcons.includes(className))) {
        svgElement.classList.add('d-none');
      }
    });

    const readingBlockHeader = contentClone.querySelector('.reading-block-header');
    if(readingBlockHeader) {
      readingBlockHeader.classList.add('d-none');
    }

    const existingResourcesContainer = contentClone.querySelector('.border-grey');

    if (existingResourcesContainer) {
      existingResourcesContainer.remove();
    }

    const resourcesContainer = document.createElement('div');
    resourcesContainer.className = 'border-grey mt-3';

    loadedHtmlResources.forEach((resource, index) => {
      if (!resource.content) return;

      const title = document.createElement('h1');
      title.className = 'mt-3';
      title.style.marginBottom = '12px';
      title.style.fontFamily = 'Poppins, sans-serif';
      title.innerHTML = getResourceTitle(
        resource.path,
        index,
        loadedHtmlResources.length
      );

      const resourceContent = document.createElement('div');
      resourceContent.style.marginBottom = '1em';
      resourceContent.innerHTML = resource.content;

      resourcesContainer.appendChild(title);
      resourcesContainer.appendChild(resourceContent);
    });

    const questionText = contentClone.querySelector('.questionWrapper');

    if (resourcesContainer.children.length > 0) {
      if (questionText) {
        questionText.parentNode.insertBefore(resourcesContainer, questionText);
      } else {
        contentClone.appendChild(resourcesContainer);
      }
    }

    fitHtmlResourceTables(resourcesContainer);
    fitHtmlQuestionTablesForPrint(contentClone);

    const questionLanguageButtons = contentClone.querySelectorAll(
      'input[name="question-toggle-btn"]'
    );

    questionLanguageButtons.forEach((button) => {
      const isSelected = Number(button.value) === Number(selectedLanguage.question);

      button.checked = isSelected;

      if (isSelected) {
        button.setAttribute('checked', '');
      } else {
        button.removeAttribute('checked');
      }
    });

    contentClone.querySelectorAll('.w-min-max-130').forEach((el) => {
      el.style.setProperty('overflow-wrap', 'break-word', 'important');
      el.style.setProperty('word-break', 'break-word', 'important');
      el.style.setProperty('white-space', 'normal', 'important');

      const table = el.closest('table');
      if (table) {
        table.style.setProperty('table-layout', 'fixed', 'important');
      }
    });

    contentClone.querySelectorAll('.custom-table.table-responsive, .not-assigned-table').forEach((el) => {
      el.style.setProperty('max-height', 'none', 'important');
      el.style.setProperty('height', 'auto', 'important');
      el.style.setProperty('overflow', 'visible', 'important');
    });

    contentClone.querySelectorAll('table.currentlist-table').forEach((el) => {
      el.style.setProperty('table-layout', 'fixed', 'important');
      el.style.setProperty('position', 'static', 'important');
    });

    await printPDF('QuestionPreview', contentClone.innerHTML, PageSize.A4);
  };

  const loadAllHtmlResources = async () => {
    const isSpanish =
    selectedLanguage.resources === Languages.Spanish &&
    props.questionDetails?.question?.hasQuestionFilesInSpanish;

    const resources = isSpanish
      ? [...(props.questionDetails?.question?.spanishVersion?.resources ?? [])]
      : [...htmResources];

    const loadedResources = await Promise.all(
      resources.map(async resource => {
        if (resource.content || !resource.path) {
          return resource;
        }

        try {
          const result = await getHtmlContent({
            resourceId: resource.resourceId,
            filePath: resource.path
          });

          if (result.error) {
            return {
              ...resource,
              content: result.error.data.error
            };
          }

          return {
            ...resource,
            content: result.data.content,
            isBooklet: result.data.isBooklet,
            bookletCoverUrl: result.data.bookletCoverUrl
          };
        } catch (error) {
          console.error('Failed to load HTML resource:', error);
          return resource;
        }
      })
    );

    return {
      resources: loadedResources
    };
  };

  function removePTags(inputString) {
    return inputString.replace(/^<p>|<\/p>$/g, '');
  }

  const handleUpdateMaxPoint = async (maxPoint) => {
    if (!maxPoint || maxPoint <= 0 || maxPoint > 100 || isSaving)
      return;
    setUpdatedMaxPointError('');
    setMaxPointUpdateMessage('Saving...');
    setIsSaving(true);
    const options = {
      questionId: props.questionDetails?.questionId,
      assignmentId: props.assignmentId,
      maxPoint: maxPoint
    };

    var response = await setQuestionPoint(options);
    setIsSaving(false);
    if (response?.error) {
      setMaxPointUpdateMessage('');
      if (response.error.data?.errors)
        CustomToast(response.error.data.errors.maxPoint[0], 'error');
      else
        CustomToast(response.error.data.error, 'error');
      return;
    }
    setMaxPointUpdateMessage('Value updated successfully');

    setTimeout(() => {
      setMaxPointUpdateMessage('');
    }, 3000);
  };

  const handleUpdateMaxPointChange = (e) => {
    const value = e.target.value;
    setUpdatedMaxPoint(value);

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const newTimeout = setTimeout(() => {
      if (value >= 1 && value <= 100) {
        setUpdatedMaxPointError('');
        handleUpdateMaxPoint(value);
      } else {
        setUpdatedMaxPointError('Please enter a number between 1 and 100.');
      }
    }, 500);

    setDebounceTimeout(newTimeout);
  };

  const handleUpdateMaxPointBlur = () => {
    if (!updatedMaxPoint || updatedMaxPoint == 0) {
      setUpdatedMaxPointError('Please enter a number between 1 and 100.');
    }
  };

  const handleResourceExpand = async (selectedIndex) => {
    let tempResources = selectedLanguage.resources === Languages.English || !props.questionDetails?.question?.hasQuestionFilesInSpanish ? [...htmResources] : [...(props.questionDetails.question.spanishVersion?.resources ?? [])];
    let selectedResource = { ...tempResources[selectedIndex] };
    selectedResource.isExpanded = !selectedResource.isExpanded;

    if (!selectedResource.content && selectedResource.path) {
      const options = {
        resourceId: selectedResource.resourceId,
        filePath: selectedResource.filePath ?? selectedResource.path
      };
      const result = await getHtmlContent(options);
      if (result.error)
        selectedResource.content = result.error.data.error;
      else {
        if (result.data.isBooklet) {
          selectedResource.isBooklet = true;
          selectedResource.bookletCoverUrl = result.data.bookletCoverUrl;
          selectedResource.content = result.data.content;
        }
        else {
          selectedResource.content = result.data.content;
        }
      }
    }

    if (selectedResource.content)
      selectedResource.content = fitHtmlResourceContent(selectedResource.content);

    tempResources[selectedIndex] = selectedResource;
    if(selectedLanguage.resources === Languages.Spanish && props.questionDetails?.question?.hasQuestionFilesInSpanish)
      updateSpanishVersion('resources', tempResources);
    else
      setHtmResources(tempResources);
  };

  const resetBookletStateValues = () => {
    const updatedArray = htmResources.map(obj => ({
      ...obj,
      isExpanded: false
    }));
    setHtmResources(updatedArray);
  };

  const renderMcAnswers = (mcAnswers) => {
    const isPreviewMode = props.type === QuestionPreviewTypes.classRoomPreview || props.type === QuestionPreviewTypes.assignmentReportPreview;

    const getAnswerLabel = (index, answerText) => {
      const prefix = props.mcStyle === 0
        ? `${index + 1}.  `
        : props.mcStyle === 1
          ? `${String.fromCharCode(97 + index).toUpperCase()}.  `
          : `${String.fromCharCode(97 + index)}.  `;
      return `${prefix} ${removePTags(answerText)}`;
    };

    const renderAnswerRow = (answer, index, { name, checked, showCorrectLabel, className, ref }) => (
      <div className={`${className} form-check d-flex`} ref={ref}>
        <input
          className="form-check-input me-2"
          type="radio"
          name={name}
          value={answer.isCorrect == 1 ? 'correct' : 'incorrect'}
          checked={checked}
        />
        <label
          className="form-check-label answer-label me-1"
          dangerouslySetInnerHTML={{ __html: getAnswerLabel(index, answer.answer) }}></label>
        <span style={{ color: 'green', marginTop: '3px' }}>
          {showCorrectLabel ? ' (Correct)' : ''}
        </span>
      </div>
    );

    return mcAnswers?.map((answer, index) => {
      const correct = answer.isCorrect == 1;
      const screenChecked = isPreviewMode ? showAnswer && correct : correct;

      return (
        <React.Fragment key={index}>
          {renderAnswerRow(answer, index, {
            name: 'flexRadioDefault22',
            checked: screenChecked,
            showCorrectLabel: screenChecked,
            className: 'no-print',
            ref: formCheckRef
          })}
          {renderAnswerRow(answer, index, {
            name: 'flexRadioDefault2',
            checked: correct,
            showCorrectLabel: correct,
            className: 'mc-answer d-none'
          })}
        </React.Fragment>
      );
    });
  };

  const handleSpanishRequests = async (contentName) => {
    switch (contentName) {
    case 'question':
      await getSpanishQuestionTextAsync();
      break;
    case 'mcAnswer':
      await getSpanishMCAnswerAsync();
      break;
    case 'hint':
      await getSpanishHintAsync();
      break;
    case 'reason':
      await getSpanishReasonAsync();
      break;
    case 'sampleAnswer':
      await getSpanishSampleAnswerAsync();
      break;
    case 'resources':
      await getSpanishQuestionResourcesAsync();
    }
  };

  const handleLanguageChange = (section, value) => {
    setSelectedLanguage(prev => ({
      ...prev,
      [section]: Number(value),
    }));

    if(Number(value) == Languages.Spanish)
      handleSpanishRequests(section);
  };

  const updateSpanishVersion = (field, value) => {
    props.setQuestionDetails(prev => {
      if (!prev?.question) return prev;

      return {...prev,
        question: {...prev.question,
          spanishVersion: {
            ...prev.question?.spanishVersion,
            [field]: value
          }
        }
      };
    });
  };

  const getSpanishHintAsync = async () => {
    if (props.questionDetails?.question?.spanishVersion?.hint) return;

    try {
      const options = {
        id: props.questionDetails?.questionId,
        languageId: Languages.Spanish
      };

      let response = await getQuestionHint(options).unwrap();
      if (response.hint) {
        updateSpanishVersion('hint', response.hint);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getSpanishReasonAsync = async () => {
    if (props.questionDetails?.question?.spanishVersion?.reason) return;

    try {
      const options = {
        id: props.questionDetails?.questionId,
        languageId: Languages.Spanish
      };

      let response = await getQuestionReason(options).unwrap();
      if (response.reason) {
        updateSpanishVersion('reason', response.reason);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getSpanishSampleAnswerAsync = async () => {
    if (props.questionDetails?.question?.spanishVersion?.sampleAnswer) return;

    try {
      const options = {
        id: props.questionDetails?.questionId,
        languageId: Languages.Spanish
      };

      let response = await getQuestionSampleAnswer(options).unwrap();
      if (response.sampleAnswer) {
        updateSpanishVersion('sampleAnswer', response.sampleAnswer);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getSpanishMCAnswerAsync = async () => {
    if (props.questionDetails?.question?.spanishVersion?.mcAnswer) return;

    try {
      const options = {
        id: props.questionDetails?.questionId,
        languageId: Languages.Spanish
      };

      let response = await getQuestionMCAnswer(options).unwrap();
      if (response.mcAnswer) {
        updateSpanishVersion('mcAnswer', response.mcAnswer);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getSpanishQuestionTextAsync = async () => {
    if (props.questionDetails?.question?.spanishVersion?.question) return;

    try {
      const options = {
        id: props.questionDetails?.questionId,
        languageId: Languages.Spanish
      };

      let response = await getQuestionText(options).unwrap();
      if (response.question) {
        updateSpanishVersion('question', response.question);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getSpanishQuestionResourcesAsync = async () => {
    if (props.questionDetails?.question?.spanishVersion?.resources?.length > 0) return;

    try {
      const options = {
        id: props.questionDetails?.questionId,
        languageId: Languages.Spanish
      };

      let response = await getQuestionResources(options).unwrap();
      if (response.questionFiles?.length) {
        updateSpanishVersion('resources', response.questionFiles.filter(r => r.endsWith('.htm')).map(r => ({ path: r })));
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    setSelectedLanguage({
      question: Languages.English,
      hint: Languages.English,
      reason: Languages.English,
      mcAnswer: Languages.English,
      sampleAnswer: Languages.English,
      resources: Languages.English
    });
    setHintDetails(false);
    setReasonDetail(false);
    setSampleAnswerDetails(false);
    setAnswerDetails(false);
  }, [props.questionDetails?.questionId, props.isLoading]);

  const handleQuestionLanguageChange = (val) => {
    handleLanguageChange('question', val);
    if(props.type != QuestionPreviewTypes.questionPreview || props.previewOptions?.showHtmResources)
      handleLanguageChange('resources', val);
  };

  function getResourceTitle(resourcePath, index, totalCount) {
    const fileName = resourcePath?.split('/').pop()?.toLowerCase();

    const matchedSet = props.questionDetails?.question?.setData?.find((set) =>
      set.setDocuments?.some((doc) => doc.filename?.toLowerCase() === fileName)
    );

    if (!matchedSet?.description) {
      return `Passage ${index + 1}`;
    }

    const cleanSetName = removePTags(matchedSet.description);
    return totalCount > 1 ? `${cleanSetName} (${index + 1})` : cleanSetName;
  }

  return (
    <>
      {props.isLoading ?
        <SkeletonGrid
          rowCount={props.previewOptions?.skeletonCount || 6}
          colCount={1}
          className='p-3 mb-2 mt-4'
        />
        :
        <>
          <div
            id="content"
            className={`overflow-auto questions-details ${props.isFromQset ? '' : 'ps-5 pt-4'} w-100`}>
            <div className={`d-flex ${props.type != QuestionPreviewTypes.questionPreview || props.previewOptions?.showHtmResources ? 'justify-content-between' : 'justify-content-end'}`}>
              {(props.type != QuestionPreviewTypes.questionPreview || props.previewOptions?.showHtmResources) &&
                <div className='d-flex flex-row gap-2 align-items-center'>
                  {!props.isFromQset &&
                    <h4>Question {props.type != QuestionPreviewTypes.questionPreview && props.type != QuestionPreviewTypes.assignmentReportPreview && <label>{props.selectedQuestionId + 1}</label>}</h4>
                  }
                  <div>{htmResources?.length > 0 && (
                    <>
                      <div
                        className={`${props.isFromQset && 'mb-4'} d-flex reading-block-header z-index10 position-relative`}
                        onClick={() => {setReadingPassage(!readingPassage); resetBookletStateValues();}}>
                        <FontAwesomeIcon className="mx-2" color="#F7931E" icon={faBookOpen} />
                        <div style={{ color: '#F7931E' }}>View Question File/Passage</div>
                      </div>
                    </>
                  )}
                  </div>
                </div>
              }
              <div className='align-items-end d-flex flex-column-reverse'>
                {(props.questionDetails?.question?.hasQuestionInSpanish || props.questionDetails?.question?.hasQuestionFilesInSpanish) &&
                  <LanguageToggleSwitch
                    name={'question'}
                    selectedValue={selectedLanguage.question}
                    onChange={(val) => handleQuestionLanguageChange(val)}
                  />
                }
              </div>
            </div>
            {audioResources?.length > 0 &&
                  <div className="d-flex align-items-center gap-2 mt-3 mb-2 audio-notice">
                    <FontAwesomeIcon icon={faCircleInfo} className="text-primary" style={{ width: '1rem', height: '1rem' }} />
                    <span className='audio-notice-description'>&quot;Students will not see the reading selection text and will have to listen to the audio file.&quot;</span>
                  </div>
            }
            {readingPassage &&
              <>
                {isQuestionResourceLoading ?
                  <SkeletonGrid
                    rowCount={3}
                    colCount={1}
                    className='p-1 mb-2 mt-2'
                  />
                  :          
                  <div className='border-grey mt-3'>
                    <div>
                      <div className="d-flex pb-4" style={{ flexDirection: 'column' }}> 
                        <>
                          {(selectedLanguage.resources === Languages.Spanish && props.questionDetails?.question?.hasQuestionFilesInSpanish ? props.questionDetails?.question?.spanishVersion?.resources : htmResources)?.map((resource, index) => (
                            <>
                              <div className='d-flex align-items-center gap-2 fs-4 cursor-pointer pb-3' key={index}>
                                <FontAwesomeIcon className='text-orange z-index10 position-relative' icon={resource.isExpanded ? faMinusCircle : faPlusCircle} onClick={() => handleResourceExpand(index)} />
                                <h1 className='m-0' dangerouslySetInnerHTML={{
                                  __html: getResourceTitle(
                                    resource.path,
                                    index,
                                    (selectedLanguage.resources === Languages.Spanish && props.questionDetails?.question?.hasQuestionFilesInSpanish
                                      ? props.questionDetails?.question?.spanishVersion?.resources
                                      : htmResources)?.length
                                  )
                                }}></h1>
                              </div>
                              {resource.isBooklet ?
                                (
                                  <BookletViewer
                                    content={resource.content}
                                    coverUrl={resource.bookletCoverUrl}
                                    startClicked={resource.bookletStartClicked}
                                    resourceIndex={index}
                                    htmResources={htmResources}
                                    setHtmResources={setHtmResources}
                                    isCollapsed={!resource.isExpanded}
                                  />
                                ):(
                                  <>
                                    {resource.isExpanded &&
                                    <div className='html-resource-content'
                                      key={index}
                                      id={'htmlRes' + index}
                                      dangerouslySetInnerHTML={{ __html: resource.content }}>
                                    </div>
                                    }
                                  </>
                                )}
                            </>                             
                          ))}
                        </>
                      </div>
                    </div>
                  </div>
                }
              </>
            }

            {props.questionDetails?.blockedText ? (
              <p
                dangerouslySetInnerHTML={{ __html: props.questionDetails?.blockedText }}
                className="fw-bold text-danger"></p>
            ) : (
              <>
                {isQuestionTextLoading ?
                  <SkeletonGrid
                    rowCount={6}
                    colCount={1}
                    className='p-1 mb-2 mt-2'
                  />
                  :
                  <div className='questionWrapper mt-3' dangerouslySetInnerHTML={{
                    __html: fitHtmlResourceContent(selectedLanguage.question === Languages.Spanish && props.questionDetails?.question?.spanishVersion?.question
                      ? props.questionDetails.question.spanishVersion.question
                      : props.questionDetails?.question?.question)
                  }}></div>
                }
              </>
            )}

            <div className="classroom-content pb-5">
              {type1 && (
                <>
                  <div className={`toggleDive classroom-content-box mb-4 pb-4 overflow-auto ${answerDetails && 'open'} `}>
                    <div className="classroom-content-header d-flex justify-content-between align-items-center cursor-pointer" onClick={() => setAnswerDetails(!answerDetails)}>
                      <span className="d-flex flex-row align-items-center">
                        <h4>Answer</h4>
                        {(props.type == QuestionPreviewTypes.classRoomPreview || props.type == QuestionPreviewTypes.assignmentReportPreview) &&
                          answerDetails &&
                          props.questionDetails?.question?.mcAnswer?.filter((a) => a.isCorrect == 1)
                            .length >= 1 && (
                          <span
                            className="no-print showAnswerButton mx-3 px-2 py-1"
                            onClick={(e) => {e.stopPropagation(); setShowAnswer(!showAnswer);}}>
                            {showAnswer ? 'Hide Correct' : 'Show Correct'}
                          </span>
                        )}
                      </span>
                      <button
                        className="rounded-circle z-index10">
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                    <div className={`toggleDive ${!answerDetails && 'd-none'} overflow-auto`}>
                      {answerDetails && props.questionDetails?.question?.hasMcAnswerInSpanish &&
                        <div className="mb-2 d-flex justify-content-end">
                          <LanguageToggleSwitch
                            name={'mcAnswer'}
                            selectedValue={selectedLanguage.mcAnswer}
                            onChange={(val) => handleLanguageChange('mcAnswer', val)}
                          />
                        </div>
                      }
                      <>
                        {isMCAnswerLoading ?
                          <SkeletonGrid
                            rowCount={4}
                            colCount={1}
                            className='p-1 mb-2 mt-2'
                          />
                          :
                          renderMcAnswers(selectedLanguage.mcAnswer === Languages.Spanish && props.questionDetails?.question?.spanishVersion?.mcAnswer
                            ? props.questionDetails.question.spanishVersion.mcAnswer
                            : props.questionDetails?.question?.mcAnswer)
                        }
                      </>
                    </div>
                  </div>
                  {!props.previewOptions?.hideHint &&
                  <div className={`classroom-content-box mb-4 pb-4 ${hintDetails && 'open'} `}>
                    <div className="classroom-content-header d-flex justify-content-between align-items-center cursor-pointer" onClick={() => setHintDetails(!hintDetails)}>
                      <h4>Hint</h4>
                      <button
                        className="rounded-circle z-index10">
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                    <div className={`toggleDive ${!hintDetails && 'd-none'}`}>
                      {props.questionDetails?.question?.hasHintInSpanish &&
                        <div className="mb-2 d-flex justify-content-end">
                          <LanguageToggleSwitch
                            name={'hint'}
                            selectedValue={selectedLanguage.hint}
                            onChange={(val) => handleLanguageChange('hint', val)}
                          />
                        </div>
                      }
                      {isHintLoading ?
                        <SkeletonGrid
                          rowCount={3}
                          colCount={1}
                          className='p-1 mb-2 mt-2'
                        />
                        :
                        <p dangerouslySetInnerHTML={{
                          __html: selectedLanguage.hint === Languages.Spanish && props.questionDetails?.question?.spanishVersion?.hint
                            ? props.questionDetails.question.spanishVersion.hint
                            : props.questionDetails?.question?.hint
                        }}></p>
                      }
                    </div>
                  </div>
                  }
                  {!props.previewOptions?.hideReason &&
                  <div className={`classroom-content-box mb-4 pb-4 ${reasonDetail && 'open'} `}>
                    <div className="classroom-content-header d-flex justify-content-between align-items-center cursor-pointer" onClick={() => setReasonDetail(!reasonDetail)}>
                      <h4>Reason</h4>
                      <button
                        className="rounded-circle z-index10">
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                    <div className={`toggleDive ${!reasonDetail && 'd-none'}`}>
                      {props.questionDetails?.question?.hasReasonInSpanish &&
                        <div className="mb-2 d-flex justify-content-end">
                          <LanguageToggleSwitch
                            name={'reason'}
                            selectedValue={selectedLanguage.reason}
                            onChange={(val) => handleLanguageChange('reason', val)}
                          />
                        </div>
                      }
                      {isReasonLoading ?
                        <SkeletonGrid
                          rowCount={3}
                          colCount={1}
                          className='p-1 mb-2 mt-2'
                        />
                        :
                        <div className='reasonWrapper' dangerouslySetInnerHTML={{
                          __html: fitHtmlResourceContent(selectedLanguage.reason === Languages.Spanish && props.questionDetails?.question?.spanishVersion?.reason
                            ? props.questionDetails.question.spanishVersion.reason
                            : props.questionDetails?.question?.reason)
                        }}></div>
                      }
                    </div>
                  </div>
                  }
                </>
              )}
              {type2 && (
                <>
                  <div className={`classroom-content-box mb-4 pb-4 ${sampleAnswerDetails && 'open'} `}>
                    <div className="classroom-content-header d-flex justify-content-between align-items-center cursor-pointer" onClick={() => setSampleAnswerDetails(!sampleAnswerDetails)}>
                      <h4>Sample Answer</h4>
                      <button
                        className="rounded-circle z-index10">
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                    <div className={`toggleDive ${!sampleAnswerDetails && 'd-none'}`}>
                      {props.questionDetails?.question?.hasSampleAnswerInSpanish &&
                        <div className="mb-2 d-flex justify-content-end">
                          <LanguageToggleSwitch
                            name={'sampleAnswer'}
                            selectedValue={selectedLanguage.sampleAnswer}
                            onChange={(val) => handleLanguageChange('sampleAnswer', val)}
                          />
                        </div>
                      }
                      {isSampleAnswerLoading ?
                        <SkeletonGrid
                          rowCount={3}
                          colCount={1}
                          className='p-1 mb-2 mt-2'
                        />
                        :
                        <p dangerouslySetInnerHTML={{
                          __html: selectedLanguage.sampleAnswer === Languages.Spanish && props.questionDetails?.question?.spanishVersion?.sampleAnswer
                            ? props.questionDetails.question.spanishVersion.sampleAnswer
                            : props.questionDetails?.question?.sampleAnswer
                        }}></p>
                      }
                    </div>
                  </div>
                  {!props.previewOptions?.hideOptions &&
                  <div className={`classroom-content-box mb-4 pb-4 ${optionDetails && 'open'} `}>
                    <div className="classroom-content-header d-flex justify-content-between align-items-center cursor-pointer" onClick={() => { setOptionDetails(!optionDetails), (!optionDetails && updatedMaxPoint == null) ? setUpdatedMaxPoint(props.questionDetails?.question?.options?.maxPoint) : null; }}>
                      <h4>Options</h4>
                      <button
                        className="rounded-circle z-index10">
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                    <div className={`toggleDive ${!optionDetails && 'd-none'}`}>
                      <Row className='mb-2'>
                        <Col>Number Of Lines for the answer: {props.questionDetails?.question?.options?.answerSize}</Col>
                      </Row>

                      {(props.type != QuestionPreviewTypes.editAssignmentPreview || !props.questionDetails?.question?.options?.isEditable) &&
                        <Row><Col>{props.questionDetails?.question?.options?.isDefaultMaxPoint && props.questionDetails?.question?.options?.isEditable
                          ? 'Default Maximum Points (can be changed under Edit Assignment Options):' : 'Maximum Points:'} &nbsp;{props.questionDetails?.question?.options?.maxPoint}
                        </Col>
                        </Row>
                      }

                      {(props.type == QuestionPreviewTypes.editAssignmentPreview && props.questionDetails?.question?.options?.isEditable) &&
                        <><Row><Col className='d-flex gap-3 col-4'><div className='align-items-center d-flex col-7'><span>Maximum Points &nbsp;</span>
                          <span><OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip className="initial-assignment-tooltip">
                                To change the maximum point value for this question, enter a new value between 1 to 100 in the input box
                              </Tooltip>
                            }>
                            <FontAwesomeIcon className='circle-question' icon={faQuestionCircle} />
                          </OverlayTrigger>
                          </span>
                        </div>
                        <div className='col-5'><input
                          type='number'
                          className='form-control'
                          min="1"
                          max="100"
                          value={updatedMaxPoint}
                          onChange={handleUpdateMaxPointChange}
                          onBlur={handleUpdateMaxPointBlur}
                          onInput={() => setUpdatedMaxPointError('')}
                          onKeyDown={(e) => {
                            if (e.key === '+' || e.key === '-' || e.key === '.') {
                              e.preventDefault();
                            }
                          }}
                        /></div>
                        </Col>
                        </Row>
                        <Row><Col className='col-4 d-flex justify-content-end'>
                          {maxPointUpdateMessage.length > 0 && <span className='success-text fs-6'>{maxPointUpdateMessage}.</span>}
                        </Col></Row>
                        <Row><Col className='col-5 d-flex'>
                          {updatedMaxPointError.length > 0 && <span className='text-danger fs-6'>{updatedMaxPointError}.</span>}
                        </Col></Row>
                        </>
                      }
                      <Row className=' mt-2'>
                        <Col>Type: <span dangerouslySetInnerHTML={{
                          __html: props.questionDetails?.question?.options?.type
                        }}></span></Col>
                      </Row>
                    </div>
                  </div>
                  }
                </>
              )}
              {(type1 || type2) && (
                <>
                  {!props.isPublicAssignment && !props.previewOptions?.hideResources &&
                    <AttachResources
                      questionDetails={props.questionDetails}
                      nextQuestionHandler={props.nextQuestionHandler}
                      assignedStatus={props.assignedStatus}
                      assignmentId={props.assignmentId}
                      questionCount={props.questionCount}
                      previewType={props.type}
                      assignmentName={props.assignmentName}
                      setAssignmentDetails={props.setAssignmentDetails}
                      assignmentDetails={props.assignmentDetails}
                      prefetchedData={castleResourceData}
                      fetchCastleResources={fetchCastleResources}
                      onLoadComplete={handleResourceLoadComplete}
                    />
                  }
                  {!props.previewOptions?.hideProperties &&
                  <div className={`classroom-content-box mb-4 pb-4 ${tagAttributeStandard && 'open'} `}>
                    <div className="classroom-content-header d-flex justify-content-between align-items-center cursor-pointer" onClick={() => setTagAttributeStandard(!tagAttributeStandard)}>
                      <h4>Topic / Tag / Attribute / Standard</h4>
                      <button
                        className="rounded-circle z-index10">
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                    <div className={`toggleDive ${!tagAttributeStandard && 'd-none'}`}>
                      <h3 style={{ marginTop: '5px' }}></h3>
                      <div className="custom-table table-responsive currentlist-table not-assigned-table tags-table">
                        <table className="table currentlist-table">
                          <thead>
                            <tr>
                              <th>Topics</th>
                              <th>Tags</th>
                              <th>Attributes</th>
                              <th>Standards</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>
                                <ul>
                                  {props.questionDetails?.topics?.map((item, index) => (
                                    <li key={index}>{item}</li>
                                  ))}
                                </ul>
                              </td>
                              <td>
                                <ul>
                                  {props.questionDetails?.tags?.map((item, index) => (
                                    <li key={index}>{item}</li>
                                  ))}
                                </ul>
                              </td>
                              <td>
                                <ul>
                                  {props.questionDetails?.attributes?.map((item, index) => (
                                    <li key={index}>{item}</li>
                                  ))}
                                </ul>
                              </td>
                              <td>
                                <ul>
                                  {props.questionDetails?.standards?.map((item, index) => (
                                    <li key={index}>{item}</li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  }
                </>
              )}
            </div>
            {(props.type == QuestionPreviewTypes.questionPreview || props.type == QuestionPreviewTypes.assignmentReportPreview) && !props.isFromQset && !props.previewOptions?.hidePrint &&
              !props.isLoading &&
              !props.questionDetails?.blockedText && (
              <div className="d-flex justify-content-end me-2">
                <ProgressButton
                  isLoading={isPdfGenerating}
                  onClick={handlePrint}
                  loadingLabel="Generating PDF"
                  label="Print to PDF"
                  className="btn btn-castleRed"
                />
              </div>
            )}
            {props.type == QuestionPreviewTypes.assignmentPreview && !props.previewOptions?.hidePrevNextButton &&
              !props.isLoading && (
              <div className="d-flex gap-3 justify-content-end me-2 pb-5">
                <button
                  onClick={props.previousQuestionHandler}
                  className={`btn btn-castleWhite btn-width mt-5 ${props.isFirstQuestion ? 'disabled' : ''}`}>
                     Previous
                </button>
                <button
                  onClick={props.nextQuestionHandler}
                  className={`btn btn-castleRed btn-width mt-5 ${props.lastQuestion ? 'disabled' : ''}`}>
                    Next
                </button>
              </div>
            )}
          </div>
        </>
      }
    </>
  );
};

QuestionDetail.propTypes = {
  questionDetails: PropTypes.object,
  setQuestionDetails: PropTypes.func,
  nextQuestionHandler: PropTypes.func,
  selectedQuestionId: PropTypes.number,
  lastQuestion: PropTypes.bool,
  isFirstQuestion: PropTypes.bool,
  isLoading: PropTypes.bool,
  mcStyle: PropTypes.number,
  assignedStatus: PropTypes.number,
  assignmentId: PropTypes.number,
  questionCount: PropTypes.number,
  type: PropTypes.string,
  assignmentName: PropTypes.string,
  setAssignmentDetails: PropTypes.func,
  assignmentDetails: PropTypes.object,
  isPublicAssignment: PropTypes.bool,
  isFromQset: PropTypes.bool,
  previewOptions: PropTypes.object,
  previousQuestionHandler: PropTypes.func
};

export default QuestionDetail;
